import { createContext, useContext, useEffect, useMemo, useState } from "react";

const AuthContext = createContext(null);

const clearAuthStorage = () => {
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  } catch (error) {
    console.error("Failed to clear auth storage:", error);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const storedToken = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");

      if (storedToken && storedUser) {
        const parsedUser = JSON.parse(storedUser);

        setUser(parsedUser);
        setToken(storedToken);
        setIsAuth(true);
      } else {
        setUser(null);
        setToken(null);
        setIsAuth(false);
      }
    } catch (error) {
      console.error("Failed to parse auth data:", error);
      clearAuthStorage();
      setUser(null);
      setToken(null);
      setIsAuth(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = (userData, userToken) => {
    try {
      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("token", userToken);
    } catch (error) {
      console.error("Failed to save auth data:", error);
    }

    setUser(userData);
    setToken(userToken);
    setIsAuth(true);
  };

  const logout = () => {
    clearAuthStorage();

    setUser(null);
    setToken(null);
    setIsAuth(false);
  };

  const permissions = user?.permissions || {};
  const userType = user?.type || null;
  const userRole = user?.role || null;

  const hasPermission = (permissionName) => {
    return Number(permissions?.[permissionName]) === 1;
  };

  const hasAnyPermission = (permissionNames = []) => {
    return permissionNames.some((permission) => Number(permissions?.[permission]) === 1);
  };

  const hasAllPermissions = (permissionNames = []) => {
    return permissionNames.every((permission) => Number(permissions?.[permission]) === 1);
  };

  const hasRole = (roleName) => userRole === roleName;

  const hasType = (typeName) => userType === typeName;
const canManageOrders = () =>
  ["admin", "manager", "head_manager"].includes(userRole);

  const value = useMemo(
    () => ({
      isAuth,
      loading,
      user,
      token,
      permissions,
      userType,
      userRole,

      isAdmin: userType === "admin",
      isUser: userType === "user",

      login,
      logout,

      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      hasRole,
      hasType,
      canManageOrders,
    }),
    
    [isAuth, loading, user, token, permissions, userType, userRole ,canManageOrders,]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);